import { TrackingEvents } from '../trackingEvents';
import {
  forgetRemoteParticipants,
  noteJourneySignal,
  noteRemoteParticipant,
  recordJourneyStep,
  recordSessionEnd,
} from './journeySteps';

type EventProperties = Record<string, unknown> | undefined;

function remoteParticipantId(properties: EventProperties) {
  const participant = properties?.participant;
  const id =
    participant && typeof participant === 'object'
      ? (participant as Record<string, unknown>).participantUserId
      : undefined;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : undefined;
}

/**
 * Derives joining and in-session steps from the tracking events the app
 * already emits. `send` and `hear` are recorded at the media layer instead.
 */
export function recordJourneyStepsForTrackingEvent(
  eventName: TrackingEvents,
  properties: EventProperties,
  { isError = false }: { isError?: boolean } = {},
) {
  switch (eventName) {
    // joining
    case TrackingEvents.CLIENT_ENTER_SESSION_ROOM:
    case TrackingEvents.PROVIDER_ENTER_SESSION_ROOM:
      recordJourneyStep('joining.connect');
      return;

    // waiting (provider side); a client removed in-session records its own end
    case TrackingEvents.PROVIDER_ADMIT_CLIENT:
      recordJourneyStep('waiting.admitted', {
        repeatable: true,
        participant: typeof properties?.patientShortId === 'string' ? properties.patientShortId : undefined,
      });
      return;

    // room occupancy, for telling a re-admit or an additional client from the next session
    case TrackingEvents.REMOTE_PARTICIPANT_CONNECTED:
    case TrackingEvents.REMOTE_PARTICIPANT_RECONNECTED: {
      const id = remoteParticipantId(properties);
      if (id !== undefined) noteRemoteParticipant(id, 'connected');
      return;
    }
    case TrackingEvents.REMOTE_PARTICIPANT_DISCONNECTED: {
      const id = remoteParticipantId(properties);
      if (id !== undefined) noteRemoteParticipant(id, 'disconnected');
      return;
    }

    // signals: counted on the end event; the first disconnect or connectivity
    // issue also fails its quality step
    case TrackingEvents.ROOM_RECONNECTING:
      noteJourneySignal('reconnecting');
      return;
    case TrackingEvents.ROOM_RECONNECTED:
      noteJourneySignal('reconnected');
      return;
    case TrackingEvents.ROOM_REJOINING:
      noteJourneySignal('rejoining');
      return;
    case TrackingEvents.ROOM_DISCONNECTED:
      forgetRemoteParticipants();
      if (isError) noteJourneySignal('unplanned_disconnect');
      return;
    case TrackingEvents.ANY_CONNECTIVITY_ISSUE_NOTIF:
      noteJourneySignal('connectivity_issue');
      return;

    // tools
    case TrackingEvents.ANY_MESSAGE_SEND:
      recordJourneyStep('in_session.tools_work', { repeatable: true, tool: 'chat' });
      return;
    case TrackingEvents.ANY_SCREEN_SHARE:
      recordJourneyStep('in_session.tools_work', { repeatable: true, tool: 'screen_share' });
      return;
    case TrackingEvents.ZOOM_LIVE_TRANSCRIPTION_INGEST_SUCCESS:
      recordJourneyStep('in_session.tools_work', { repeatable: true, tool: 'transcription' });
      return;
    case TrackingEvents.ZOOM_LIVE_TRANSCRIPTION_INGEST_ERROR:
    case TrackingEvents.ZOOM_LIVE_TRANSCRIPTION_BATCH_FLUSH_FAILED:
    case TrackingEvents.ZOOM_LIVE_TRANSCRIPTION_FINAL_FLUSH_FAILED:
      recordJourneyStep('in_session.tools_work', { status: 'error', tool: 'transcription' });
      return;
    case TrackingEvents.AUDIO_RECORDING_ERROR:
      recordJourneyStep('in_session.tools_work', { status: 'error', tool: 'recording' });
      return;

    // end
    case TrackingEvents.PROVIDER_SESSION_COMPLETE:
      recordSessionEnd({ reason: 'complete' });
      return;
    case TrackingEvents.PROVIDER_SESSION_EARLY_END_COMPLETE:
      recordSessionEnd({ reason: 'early_end' });
      return;
    case TrackingEvents.PROVIDER_END_AND_START_NEW_SESSION:
      recordSessionEnd({ reason: 'end_and_start_new' });
      return;
    case TrackingEvents.CLIENT_POST_SESSION_REDIRECT:
      recordSessionEnd({ reason: 'post_session_redirect' });
      return;
    default:
      return;
  }
}
